import { format, parseISO } from "date-fns";
import { graphql, useStaticQuery } from "gatsby";
import React, { FunctionComponent, useState } from "react";
import Navbar from "../../components/navbar";
import Context from "../../context/Context";
import { useEventContext } from "../../context/EventProvider";
import formatTime from "../../hooks/formatTime";
import splitTitle from "../../hooks/splitTitle";
import { LayoutProps, Speaker } from "../../interfaces/interfaces";
import useModalStore from "../../store/useModalStore";
import Footer from "../footer";
import { LoginModal } from "../modals";
import ConfirmModal from "../modals/confirmModal";
import { GlobalStyle, StyledLayout } from "./layoutStyles";

const Layout: FunctionComponent<LayoutProps> = ({ children, className }) => {
  const {
    event,
    sessions,
    speakers,
    sponsors,
  } = useEventContext();
  const { modalType } = useModalStore();
  const {
    site: { siteMetadata },
  } = useStaticQuery(query);
  const [activeLink, setActiveLink] = useState<string>("home");
  const eventId = event?.eventId;
  const hashtag = event?.hashtag;
  const singlePage = event?.singlePage;
  const email = event?.email;
  const emailSubject = event?.emailSubject;
  const siteName = event?.siteName;
  const title = event?.title;
  const isSpeakerBioEnabled = event?.isSpeakerBioEnabled;
  const sponsorLevelsFormatted = event?.levels.map((level) => level.name.toLowerCase());
  const sessionsFormatted = sessions && sessions.map((session) => {
    const {
      abstract,
      starts,
      ends,
      title,
      speakers,
      buzzWord,
      slug,
      videoId,
      image,
    } = session;
    const parsedDate = parseISO(starts);
    const date = format(parsedDate, "MM/dd/yyyy");
    const imageUrl = image ? `${image.url}` : null;
    const tile = { buzzWord, image: imageUrl, slug, videoId };
    const formatSpeakers = speakers.map(speaker => {
    const { person, title, is_moderator } = speaker;

      // Check if the person object exists before destructuring
      if (person) {
        const { first_name, last_name } = person;
        return {
          name: `${first_name} ${last_name}`,
          title,
          isModerator: is_moderator
        };
      } else {
        // Handle case where person is null or undefined
        return {
          name: 'Unknown',
          title,
          isModerator: is_moderator
        };
      }
    });

    return {
      abstract,
      date,
      startTime: formatTime(starts),
      endTime: formatTime(ends),
      title,
      speakers: formatSpeakers,
      tile,
    };
  });
  const speakersFormatted: Speaker[] = speakers.map((speaker) => {
    const {
      bio,
      title,
      image,
      person: { first_name, last_name },
      priority,
      isPendingApproval
    } = speaker;
    const imageUrl = image?.url;
    const speakerName = `${first_name} ${last_name}${isPendingApproval ? "*" : ""}`;
    return {
      bio,
      title,
      image: imageUrl,
      name: speakerName,
      priority,
      isPendingApproval,
    };
  }).sort((a, b) => a.priority - b.priority);
  const sponsorsFormatted = sponsors && sponsors.map((sponsor) => {
    const { email, logo, name, slug, sponsorLevel, url, width } = sponsor;
    const imageUrl = logo?.url;
    return {
      name,
      logo: imageUrl,
      email,
      slug,
      url,
      width: `${width}%`,
      sponsorLevel: sponsorLevel?.[0]?.name,
    };
  });
  const allContextData = {
    ...siteMetadata,
    eventId,
    hashtag,
    singlePage,
    email,
    emailSubject,
    siteName,
    title,
    sessions: sessionsFormatted,
    speakers: speakersFormatted,
    sponsors: sponsorsFormatted,
    sponsorLevels: sponsorLevelsFormatted,
    activeLink,
    setActiveLink,
    isSpeakerBioEnabled,
  };

  return (
    <Context.Provider value={allContextData}>
      <StyledLayout className={className}>
        <GlobalStyle />
        {modalType == 'login' ? <LoginModal /> : <ConfirmModal />}
        <Navbar />
        <div className="content">{children}</div>
        <Footer />
      </StyledLayout>
    </Context.Provider>
  );
};

export const query = graphql`
  query LayoutQuery {
    site {
      siteMetadata {
        navPagesSingle {
          disabled
          name
          path
          target
          auth
          showAfterEvent
        }
        navPagesDefault {
          disabled
          name
          path
          target
          auth
          showAfterEvent
        }
        socialShare {
          alt
          img
          path
        }
      }
    }
  }
`;

export default Layout;
